/**
 * Session Top Bar Component
 */

import React, { useEffect, useState } from "react";
import { useSessionStore } from "../store/sessionStore";
import { buildShareUrl, SMOKE_MODE } from "../config";
import { apiClient } from "../api/client";
import JamroomIconButton from "./JamroomIconButton";
import "./SessionTopBar.css";

const formatDurationMs = (ms: number): string => {
  const clamped = Math.max(0, Math.floor(ms));
  const totalSec = Math.floor(clamped / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

const SessionTopBar: React.FC = () => {
  const {
    title,
    setTitle,
    usersOnline,
    permissions,
    loading,
    error,
    wsConnected,
    sessionId,
    isPrivate,
    shareCode,
    createdAt,
    expiresAt,
    serverTimeOffsetMs,
    sessionExpired,
    participantName,
  } =
    useSessionStore();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (!expiresAt) return;
    const id = window.setInterval(() => setTick((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const handleTitleSave = () => {
    if (editValue.trim()) {
      setTitle(editValue);
      // TODO: POST to server to update title (owner only)
    }
    setIsEditingTitle(false);
  };

  const handleShare = async () => {
    const currentSessionId = sessionId || window.location.pathname.split("/")[2];

    if (!currentSessionId) {
      alert("Session is not ready yet.");
      return;
    }

    const url = buildShareUrl(currentSessionId);

    const sharePayload = isPrivate && shareCode ? `${url}\nCode: ${shareCode}` : url;

    try {
      await navigator.clipboard.writeText(sharePayload);
      alert("Link copied to clipboard!");
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = sharePayload;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        alert("Link copied to clipboard!");
      } catch {
        alert(`Copy failed. Share this URL manually:\n${url}`);
      }
    }
  };

  const handleDeleteSession = async () => {
    const currentSessionId = sessionId || window.location.pathname.split("/")[2];
    if (!currentSessionId) {
      alert("Session is not ready yet.");
      return;
    }
    if (currentSessionId === "demo") {
      alert("Demo session cannot be deleted.");
      return;
    }
    if (!permissions.isOwner) {
      alert("Owner permissions required.");
      return;
    }

    const ok = window.confirm(
      `Delete this session?\n\nThis will remove the session and its audio from storage.\nSession: ${currentSessionId}`
    );
    if (!ok) return;

    try {
      await apiClient.deleteSession(currentSessionId);
      window.location.assign("/");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Failed to delete session: ${msg}`);
    }
  };

  const connectionLabel = wsConnected ? "Connected" : "Disconnected";
  const syncLabel = loading ? "Syncing..." : error ? "Sync Error" : wsConnected ? "Sync OK" : "Sync Limited";
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const expiresText = expiresDate ? expiresDate.toLocaleString() : null;
  const expiresShort = expiresDate
    ? expiresDate.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const createdAtMs = createdAt ? Date.parse(createdAt) : NaN;
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : NaN;
  const nowMs = Date.now() + (serverTimeOffsetMs || 0) + tick * 0;
  const remainingMs = Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - nowMs) : null;
  const totalMs =
    Number.isFinite(createdAtMs) && Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - createdAtMs) : null;
  const remainingLabel = remainingMs === null ? null : formatDurationMs(remainingMs);
  const totalLabel = totalMs === null ? null : formatDurationMs(totalMs);

  return (
    <div className="session-topbar">
      <div className="topbar-left">
        <div className="topbar-titlegroup">
          <div className="logo" title="JamRoom">
            <img className="logo-mark" src="/branding/jamroom-mark-180.png" alt="" aria-hidden="true" />
            <img className="logo-wordmark" src="/branding/jamroom-wordmark.png" alt="JamRoom" />
          </div>
          {isEditingTitle && permissions.isOwner ? (
            <input
              type="text"
              className="title-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleSave();
                if (e.key === "Escape") setIsEditingTitle(false);
              }}
              autoFocus
            />
          ) : (
            <div
              className="session-title"
              onClick={() => permissions.isOwner && setIsEditingTitle(true)}
              title={permissions.isOwner ? "Click to edit" : ""}
            >
              {title}
            </div>
          )}
          <div className={`status-pill ${isPrivate ? "warn" : "ok"}`}>{isPrivate ? "Private" : "Public"}</div>
          {SMOKE_MODE && <div className="status-pill warn">SMOKE MODE</div>}
        </div>

        {(totalLabel || remainingLabel || expiresText || sessionExpired) && (
          <>
            <div className="topbar-divider" aria-hidden="true" />
            <div className="topbar-timegroup" aria-label="Session time">
              {totalLabel && (
                <div className="status-pill" title={`Total assigned duration: ${totalLabel}`}>
                  Total: {totalLabel}
                </div>
              )}
              {sessionExpired && <div className="status-pill error">Expired</div>}
              {!sessionExpired && remainingLabel && (
                <div className="status-pill warn" title="Time remaining (server clock)">
                  Remaining: {remainingLabel}
                </div>
              )}
              {expiresShort && expiresText && (
                <div className="status-pill warn" title={`Session expires at ${expiresText}`}>
                  Expires: {expiresShort}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="topbar-center topbar-group" aria-label="Connection status">
        <div className={`status-pill ${wsConnected ? "ok" : "warn"}`}>{connectionLabel}</div>
        <div className={`status-pill ${error ? "error" : loading ? "warn" : "ok"}`} title={error || ""}>
          {syncLabel}
        </div>
      </div>

      <div className="topbar-right">
        <div className="topbar-actions topbar-group" aria-label="Session actions">
          <JamroomIconButton
            iconId="jr-share"
            ariaLabel="Share"
            title="Copy share link"
            onClick={handleShare}
            className="share-btn"
            size={36}
          />
          {permissions.isOwner && sessionId && sessionId !== "demo" && (
            <button className="jr-btn jr-btn--danger danger-btn" onClick={handleDeleteSession} title="Delete this session">
              Delete
            </button>
          )}
        </div>

        <div className="topbar-divider" aria-hidden="true" />

        <div className="topbar-stats" aria-label="Session stats">
          <div className="status-pill" title="Users online">
            {usersOnline} online
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTopBar;
